import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { Rss } from 'lucide-react';
import { InfiniteScroll } from '@/components/ui/infinite-scroll';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { setShowFeedSheet } from '@/redux/global/global.slice';
import { IGlobalState } from '@/redux/global/global';
import { useGetActivityFeedQuery } from '@/redux/activity/activity.api.slice';
import { IActivityFeedPage } from '@/redux/activity/activity.d';
import ActivityCard from './ActivityCard';
import useSeenReporter from './useSeenReporter';
import { groupByAge } from './feedSections';

const SKELETON_KEYS = ['s1', 's2', 's3', 's4', 's5', 's6'];

function FeedSkeleton() {
  return (
    <div className="flex flex-col gap-1 px-2 py-3">
      {SKELETON_KEYS.map((key) => (
        <Skeleton key={key} className="h-16 w-full rounded-lg" />
      ))}
    </div>
  );
}

function EmptyFeed() {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-16 text-center">
      <Rss className="h-8 w-8 text-muted-foreground" />
      <p className="font-semibold text-heading">Nothing here yet</p>
      <p className="max-w-sm text-sm text-muted-foreground">
        When someone makes, comments on, or follows your work, or a creator you
        follow publishes something new, it shows up here.
      </p>
      <Link
        to="/"
        className="text-sm font-semibold text-green-700 hover:underline"
      >
        Explore projects
      </Link>
    </div>
  );
}

interface FeedBodyProps {
  isLoading: boolean;
  isFetching: boolean;
  page: IActivityFeedPage | undefined;
  onLoadMore: () => Promise<void>;
}

function FeedBody({ isLoading, isFetching, page, onLoadMore }: FeedBodyProps) {
  const reportSeen = useSeenReporter();
  if (isLoading) return <FeedSkeleton />;
  if (!page || page.groups.length === 0) return <EmptyFeed />;
  return (
    <InfiniteScroll
      onLoadMore={onLoadMore}
      hasMore={page.hasMore}
      isLoading={isFetching}
    >
      {groupByAge(page.groups).map((section) => (
        <section key={section.label} aria-label={section.label}>
          <h3 className="sticky top-0 z-30 bg-background px-5 pb-2 pt-6 text-base font-bold text-heading">
            {section.label}
          </h3>
          <ul>
            {section.groups.map((group) => (
              <ActivityCard key={group.key} group={group} onSeen={reportSeen} />
            ))}
          </ul>
        </section>
      ))}
    </InfiniteScroll>
  );
}

function FeedList() {
  const [cursor, setCursor] = useState<string | undefined>();
  const { data, isLoading, isFetching } = useGetActivityFeedQuery(cursor, {
    refetchOnMountOrArgChange: true,
  });
  const nextCursor = data?.nextCursor;

  const loadMore = useCallback(async () => {
    if (nextCursor) setCursor(nextCursor);
  }, [nextCursor]);

  return (
    <FeedBody
      isLoading={isLoading}
      isFetching={isFetching}
      page={data}
      onLoadMore={loadMore}
    />
  );
}

/** Rendered once by the navbar; the tile and the mobile menu row only open it. */
function FeedSheet() {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const open = useSelector(
    (state: { global: IGlobalState }) => state.global.showFeedSheet
  );

  // a card link navigates away; the sheet should not follow
  useEffect(() => {
    dispatch(setShowFeedSheet(false));
  }, [pathname, dispatch]);

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => dispatch(setShowFeedSheet(next))}
    >
      <SheetContent className="flex w-full flex-col gap-0 p-0 sm:max-w-md">
        <SheetHeader className="border-b p-4 pr-12 text-left">
          <SheetTitle className="text-xl font-bold text-heading">
            Feed
          </SheetTitle>
          <SheetDescription className="sr-only">
            Activity on your work and from creators you follow
          </SheetDescription>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto">
          <FeedList />
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default FeedSheet;
